import React, { useState, useEffect, useRef } from 'react';
import { api, resolveUrl } from '../services/api';
import { Shield, LogOut, Plus, Trash2, Edit3, Save, X, Users, Newspaper, Database, Upload, UserPlus, Eye } from 'lucide-react';

const emptyNews = () => ({ slug: '', category: '', date: new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }), title_id: '', title_en: '', excerpt_id: '', excerpt_en: '', content_id: '', content_en: '', image: '', gallery: '', author: 'Humas YPPSDP' });
const emptyLead = () => ({ name: '', title_id: '', title_en: '', bio_id: '', bio_en: '', image: '' });
const emptyUser = () => ({ username: '', password: '', role: 'viewer' });

const roleColor = { admin: 'bg-red-100 text-red-700', editor: 'bg-blue-100 text-blue-700', viewer: 'bg-gray-100 text-gray-700' };

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem('yppsdp_token') || '');
  const [role, setRole] = useState(localStorage.getItem('yppsdp_role') || '');
  const [username, setUsername] = useState(localStorage.getItem('yppsdp_username') || '');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [tab, setTab] = useState('news');
  const [news, setNews] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyNews());
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const authed = !!token;
  const canWrite = role === 'admin' || role === 'editor';
  const isAdmin = role === 'admin';

  useEffect(() => { if (authed) refresh(); /* eslint-disable-next-line */ }, [authed, tab]);

  const refresh = async () => {
    try {
      if (tab === 'news' || news.length === 0) setNews(await api.listNews());
      if (tab === 'leadership' || leaders.length === 0) setLeaders(await api.listLeadership());
      if (isAdmin && tab === 'users') setUsers(await api.listUsers());
    } catch (e) { /* ignore */ }
  };

  const login = async () => {
    setErr('');
    try {
      const r = await api.login(loginForm.username, loginForm.password);
      localStorage.setItem('yppsdp_token', r.token);
      localStorage.setItem('yppsdp_role', r.role);
      localStorage.setItem('yppsdp_username', r.username);
      setToken(r.token); setRole(r.role); setUsername(r.username);
    } catch (e) { setErr(e.response?.data?.detail || 'Login gagal'); }
  };

  const logout = () => {
    ['yppsdp_token', 'yppsdp_role', 'yppsdp_username'].forEach(k => localStorage.removeItem(k));
    setToken(''); setRole(''); setUsername('');
  };

  const startEdit = (item, type) => {
    if (!canWrite) return;
    setTab(type); setEditing(item.id);
    if (type === 'news') {
      setForm({
        slug: item.slug || '', category: item.category || '', date: item.date || '',
        title_id: item.title?.id || '', title_en: item.title?.en || '',
        excerpt_id: item.excerpt?.id || '', excerpt_en: item.excerpt?.en || '',
        content_id: item.content?.id || '', content_en: item.content?.en || '',
        image: item.image || '', gallery: (item.gallery || []).join('\n'), author: item.author || 'Humas YPPSDP',
      });
    } else if (type === 'leadership') {
      setForm({ name: item.name || '', title_id: item.title?.id || '', title_en: item.title?.en || '', bio_id: item.bio?.id || '', bio_en: item.bio?.en || '', image: item.image || '' });
    }
  };

  const cancel = () => { setEditing(null); setForm(tab === 'news' ? emptyNews() : tab === 'leadership' ? emptyLead() : emptyUser()); };

  const save = async () => {
    setMsg(''); setErr('');
    try {
      if (tab === 'news') {
        const payload = { slug: form.slug, category: form.category, date: form.date, title: { id: form.title_id, en: form.title_en }, excerpt: { id: form.excerpt_id, en: form.excerpt_en }, content: { id: form.content_id, en: form.content_en }, image: form.image, gallery: form.gallery.split('\n').map(s => s.trim()).filter(Boolean), author: form.author };
        if (editing && editing !== 'new') await api.updateNews(editing, payload); else await api.createNews(payload);
      } else if (tab === 'leadership') {
        const payload = { name: form.name, title: { id: form.title_id, en: form.title_en }, bio: { id: form.bio_id, en: form.bio_en }, image: form.image, order: 0 };
        if (editing && editing !== 'new') await api.updateLeadership(editing, payload); else await api.createLeadership(payload);
      } else if (tab === 'users') {
        await api.createUser(form);
      }
      setMsg('Tersimpan!'); cancel(); refresh();
      setTimeout(() => setMsg(''), 2000);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  const remove = async (idOrName) => {
    if (!window.confirm('Hapus item ini?')) return;
    try {
      if (tab === 'news') await api.deleteNews(idOrName);
      else if (tab === 'leadership') await api.deleteLeadership(idOrName);
      else if (tab === 'users') await api.deleteUser(idOrName);
      refresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Gagal hapus'); }
  };

  const seed = async () => { try { await api.seedData(true); await refresh(); setMsg('Data seed berhasil (data lama di-reset)'); setTimeout(()=>setMsg(''),2000); } catch (e) { setErr(e.response?.data?.detail || 'Gagal seed'); } };

  const fileRef = useRef();
  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const r = await api.uploadImage(file); setForm({ ...form, image: r.fullUrl }); setMsg('Gambar terupload'); setTimeout(() => setMsg(''), 2000); }
    catch (err) { setErr(err.response?.data?.detail || 'Gagal upload'); }
    e.target.value = '';
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a1929] grid place-items-center px-4">
        <div className="bg-white p-8 max-w-md w-full shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-[#d4a017]" size={32} />
            <div><div className="text-xs tracking-widest text-gray-500">YPPSDP</div><h1 className="text-2xl font-bold text-[#0a1929]" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>Admin CMS</h1></div>
          </div>
          <label className="text-xs uppercase tracking-widest text-gray-600">Username</label>
          <input value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} className="w-full mt-1 mb-3 px-3 py-2 border border-gray-300 focus:border-[#d4a017] focus:outline-none" placeholder="admin / editor / viewer" />
          <label className="text-xs uppercase tracking-widest text-gray-600">Password</label>
          <input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && login()} className="w-full mt-1 mb-3 px-3 py-2 border border-gray-300 focus:border-[#d4a017] focus:outline-none" placeholder="Password" />
          {err && <div className="text-red-600 text-sm mb-2">{err}</div>}
          <button onClick={login} className="w-full bg-[#0a1929] hover:bg-[#0f2340] text-white py-2.5 font-bold tracking-widest uppercase text-sm">Masuk</button>
          <div className="mt-5 pt-4 border-t text-xs text-gray-500 space-y-1">
            <div className="font-semibold text-gray-700">Akun default:</div>
            <div><code className="bg-gray-100 px-1">admin</code> / <code className="bg-gray-100 px-1">admin123</code> — akses penuh + kelola user</div>
            <div><code className="bg-gray-100 px-1">editor</code> / <code className="bg-gray-100 px-1">editor123</code> — tambah/edit berita & pengurus</div>
            <div><code className="bg-gray-100 px-1">viewer</code> / <code className="bg-gray-100 px-1">viewer123</code> — hanya lihat</div>
          </div>
        </div>
      </div>
    );
  }

  const items = tab === 'news' ? news : tab === 'leadership' ? leaders : users;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#0a1929] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="text-[#d4a017]" size={22} />
            <div><div className="text-[10px] tracking-widest text-[#d4a017]">YPPSDP</div><div className="font-bold">Admin CMS</div></div>
            <div className="ml-4 flex items-center gap-2">
              <span className="text-sm text-white/70">Halo, <b className="text-white">{username}</b></span>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${roleColor[role] || 'bg-gray-100 text-gray-700'}`}>{role}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-xs px-3 py-2 border border-white/20 hover:bg-white/10">Lihat Situs</a>
            {canWrite && <button onClick={seed} className="text-xs px-3 py-2 border border-white/20 hover:bg-white/10 flex items-center gap-1"><Database size={12} /> Seed</button>}
            <button onClick={logout} className="text-xs px-3 py-2 border border-white/20 hover:bg-white/10 flex items-center gap-1"><LogOut size={12} /> Keluar</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 border-b">
          <TabBtn active={tab === 'news'} onClick={() => { setTab('news'); cancel(); }} icon={Newspaper} label={`Berita (${news.length})`} />
          <TabBtn active={tab === 'leadership'} onClick={() => { setTab('leadership'); cancel(); }} icon={Users} label={`Pengurus (${leaders.length})`} />
          {isAdmin && <TabBtn active={tab === 'users'} onClick={() => { setTab('users'); cancel(); }} icon={UserPlus} label={`Pengguna (${users.length})`} />}
        </div>

        {!canWrite && (
          <div className="mb-4 px-4 py-2 bg-gray-100 border border-gray-300 text-sm flex items-center gap-2">
            <Eye size={14} /> <span>Mode Viewer — Anda hanya dapat melihat data. Hubungi admin untuk hak edit.</span>
          </div>
        )}
        {msg && <div className="mb-4 px-4 py-2 bg-[#d4a017]/20 text-[#0a1929] text-sm">{msg}</div>}
        {err && <div className="mb-4 px-4 py-2 bg-red-100 border border-red-300 text-red-700 text-sm">{err}</div>}

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-[#0a1929]">Daftar</h2>
              {canWrite && (
                <button onClick={() => { setEditing('new'); setForm(tab === 'news' ? emptyNews() : tab === 'leadership' ? emptyLead() : emptyUser()); }} className="flex items-center gap-1 text-sm bg-[#d4a017] text-[#0a1929] px-3 py-1.5 font-bold"><Plus size={14} /> Tambah</button>
              )}
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.id || it.username} className="bg-white p-3 border border-gray-200 flex items-center gap-3">
                  {tab !== 'users' && it.image && <img src={resolveUrl(it.image)} alt="" className="w-16 h-12 object-cover flex-shrink-0" />}
                  {tab === 'users' && <div className="w-10 h-10 rounded-full bg-[#0a1929] text-[#d4a017] grid place-items-center font-bold">{it.username[0].toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{tab === 'news' ? (it.title?.id || it.slug) : tab === 'leadership' ? it.name : it.username}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {tab === 'news' ? `${it.category} • ${it.date}` : tab === 'leadership' ? it.title?.id : (
                        <span className={`inline-block text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${roleColor[it.role]}`}>{it.role}</span>
                      )}
                    </div>
                  </div>
                  {canWrite && tab !== 'users' && <button onClick={() => startEdit(it, tab)} className="p-2 hover:bg-gray-100"><Edit3 size={14} /></button>}
                  {canWrite && <button onClick={() => remove(tab === 'users' ? it.username : it.id)} className="p-2 hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>}
                </div>
              ))}
              {items.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">Belum ada data.{canWrite && tab !== 'users' && ' Klik Seed untuk isi contoh.'}</div>}
            </div>
          </div>

          {canWrite && editing && (
            <div className="bg-white p-5 border border-gray-200 sticky top-24 self-start">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#0a1929]">{editing === 'new' ? 'Tambah' : 'Edit'} {tab === 'news' ? 'Berita' : tab === 'leadership' ? 'Pengurus' : 'Pengguna'}</h3>
                <button onClick={cancel} className="p-1 hover:bg-gray-100"><X size={18} /></button>
              </div>
              <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                {tab === 'news' && (<>
                  <Field label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="contoh-slug-berita" />
                  <Field label="Kategori" value={form.category} onChange={(v) => setForm({ ...form, category: v.toUpperCase() })} />
                  <Field label="Tanggal" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
                  <Field label="Judul (ID)" value={form.title_id} onChange={(v) => setForm({ ...form, title_id: v })} />
                  <Field label="Title (EN)" value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} />
                  <Field label="Ringkasan (ID)" value={form.excerpt_id} onChange={(v) => setForm({ ...form, excerpt_id: v })} textarea />
                  <Field label="Excerpt (EN)" value={form.excerpt_en} onChange={(v) => setForm({ ...form, excerpt_en: v })} textarea />
                  <Field label="Konten (ID)" value={form.content_id} onChange={(v) => setForm({ ...form, content_id: v })} textarea rows={5} />
                  <Field label="Content (EN)" value={form.content_en} onChange={(v) => setForm({ ...form, content_en: v })} textarea rows={5} />
                  <ImageField form={form} setForm={setForm} onPick={() => fileRef.current?.click()} />
                  <Field label="Galeri (satu URL per baris)" value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} textarea rows={3} />
                  <Field label="Penulis" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
                </>)}
                {tab === 'leadership' && (<>
                  <Field label="Nama" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                  <Field label="Jabatan (ID)" value={form.title_id} onChange={(v) => setForm({ ...form, title_id: v })} />
                  <Field label="Position (EN)" value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} />
                  <Field label="Biografi (ID)" value={form.bio_id} onChange={(v) => setForm({ ...form, bio_id: v })} textarea rows={4} />
                  <Field label="Bio (EN)" value={form.bio_en} onChange={(v) => setForm({ ...form, bio_en: v })} textarea rows={4} />
                  <ImageField form={form} setForm={setForm} onPick={() => fileRef.current?.click()} />
                </>)}
                {tab === 'users' && isAdmin && (<>
                  <Field label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} />
                  <Field label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
                  <div>
                    <label className="text-xs text-gray-600 font-semibold">Role</label>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-gray-300 focus:border-[#d4a017] focus:outline-none text-sm">
                      <option value="viewer">Viewer — hanya lihat</option>
                      <option value="editor">Editor — kelola konten</option>
                      <option value="admin">Admin — kelola user + konten</option>
                    </select>
                  </div>
                </>)}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
              </div>
              <button onClick={save} className="mt-4 w-full bg-[#0a1929] hover:bg-[#0f2340] text-white py-2.5 font-bold tracking-widest uppercase text-sm flex items-center justify-center gap-2"><Save size={14} /> Simpan</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label }) => (
  <button onClick={onClick} className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 ${active ? 'border-[#d4a017] text-[#0a1929]' : 'border-transparent text-gray-500 hover:text-[#0a1929]'}`}>
    <Icon size={16} /> {label}
  </button>
);

const Field = ({ label, value, onChange, textarea, rows = 2, placeholder }) => (
  <div>
    <label className="text-xs text-gray-600 font-semibold">{label}</label>
    {textarea ? (
      <textarea value={value} rows={rows} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 focus:border-[#d4a017] focus:outline-none text-sm" placeholder={placeholder} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 focus:border-[#d4a017] focus:outline-none text-sm" placeholder={placeholder} />
    )}
  </div>
);

const ImageField = ({ form, setForm, onPick }) => (
  <div>
    <label className="text-xs text-gray-600 font-semibold">Gambar utama</label>
    <div className="flex gap-2 mt-1">
      <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="flex-1 px-2 py-1.5 border border-gray-300 focus:border-[#d4a017] focus:outline-none text-sm" placeholder="URL gambar atau upload" />
      <button type="button" onClick={onPick} className="px-3 py-1.5 border border-[#0a1929] text-[#0a1929] hover:bg-[#0a1929] hover:text-white text-xs font-bold flex items-center gap-1"><Upload size={12} /> Upload</button>
    </div>
    {form.image && <img src={resolveUrl(form.image)} alt="" className="mt-2 w-32 h-20 object-cover border" />}
  </div>
);

export default Admin;
